import { html, TemplateResult } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import { WEB_COMPONENT_GZ_RM_TOP_VULNERABILITIES_SEVERITY_WIDGET } from '../../../../webcomponents.constants';
import { RmTopVulnerabilitiesBySeverityPayload } from '@nirvana/shared/dtos/types/widgets/common';
import { getRoutinesTranslator } from '../../../common/translations';
import { DEFAULT_RM_WIDGET_TOP_ROWS } from '@nirvana/shared/constants';
import { Row, RowConfig } from '../../../../ui/gzBasicTable/types/types';
import { renderText } from '../../text';
import { fillArray } from '@nirvana/shared/basic-types/array';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { ColumnKeys as VulnerabilitiesColumnKeys } from '../../../vulnerabilitiesApp/types';
import { translateFn } from '../../../../utils/translator.helper';
import { FontSizes } from '../../../../types/common/types';
import { LinkType } from '../../../../ui/gzLinkComponent/types';
import { State } from '../../../common/constants';
import { computeMaxRowsFit, fitItemsToWidget } from '../../resize';

/**
 * @summary Type here the description of this component
 * @tag gz-rm-top-vulnerabilities-severity-widget
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement(WEB_COMPONENT_GZ_RM_TOP_VULNERABILITIES_SEVERITY_WIDGET)
export class GzRmTopVulnerabilitiesSeverityWidgetWebComponent extends GzGenericWidgetContent {
  @property({ type: WidgetStateFrontEnd, attribute: true, reflect: true })
  state: WidgetStateFrontEnd;

  @property({ type: Object, attribute: true })
  payload: RmTopVulnerabilitiesBySeverityPayload = { topVulnerabilitiesBySeverity: { companyId: '', total: 0, items: [] } };

  routinesTranslator = getRoutinesTranslator();

  static get styles() {
    return styles;
  }

  protected viewAll(): void {
    NavigationHelper.navigateToRiskManagement({
      company: { id: this.payload.topVulnerabilitiesBySeverity?.companyId ?? '' },
      filters: [
        {
          [VulnerabilitiesColumnKeys.STATE]: { in: [State.ACTIVE] },
        },
        {
          [VulnerabilitiesColumnKeys.RISK_SCORE]: { range: { min: 1, max: 100 } },
        },
      ],
      section: 'vulnerabilities',
      newTab: true,
    });
  }

  protected renderLoader(): TemplateResult {
    return html` <gz-loader class="pt-1" .rows="${computeMaxRowsFit(this.contentHeight)}" columns="1"></gz-loader>`;
  }

  protected renderContent(): TemplateResult {
    const items: Row[] =
      this.payload.topVulnerabilitiesBySeverity?.items?.map(
        ({ applicationName, score }): RowConfig => ({
          data: { applicationName },
          cells: [renderText(applicationName), html` <gz-rm-severity .score="${score}" reverse></gz-rm-severity>`],
        })
      ) ?? [];

    const emptyItem = [renderText('...', true), html` <gz-rm-severity noHighlight reverse></gz-rm-severity>`];
    const fullItems = fitItemsToWidget(fillArray(items, DEFAULT_RM_WIDGET_TOP_ROWS, emptyItem), this.contentHeight);

    return html`
      <gz-basic-table
        style="margin-top: calc(var(--spacer)*0.3)"
        rowsClickable
        alignLastColumnRight
        .onClick="${(rowData: { applicationName: string }) => {
          if (!rowData) {
            return;
          }

          const { applicationName } = rowData;

          NavigationHelper.navigateToRiskManagement({
            company: { id: this.payload.topVulnerabilitiesBySeverity?.companyId ?? '' },
            section: 'vulnerabilities',
            filters: [
              {
                [VulnerabilitiesColumnKeys.NAME]: {
                  complexEnumFilter: [{ label: applicationName, value: applicationName }],
                },
              },
            ],
            newTab: true,
          });
        }}"
        .data="${fullItems}"
      ></gz-basic-table>

      <gz-link type="${LinkType.QUIET}" fontSize="${FontSizes.PARAGRAPH_11}" .callback=${() => this.viewAll()}>
        ${translateFn('View all')} (${this.payload.topVulnerabilitiesBySeverity?.total ?? 0})
      </gz-link>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-rm-top-vulnerabilities-severity-widget': GzRmTopVulnerabilitiesSeverityWidgetWebComponent;
  }
}
